import { alpha } from '@mui/material/styles';
import { ThreatLevel } from '@/reports/types/supporting';
import { LinearGaugeChart } from '@/theme/components/Chart/LinearGaugeChart';
import {
  Tooltip,
  Icons,
  Text,
  Stack,
  styled,
  useTheme,
  TextProps,
  Divider,
  Box,
  Grid,
  GridProps,
} from '@/theme/index';

export const GridItem = styled((props: GridProps) => (
  <Grid item xs={12} md={6} {...props} />
))({});

export const FraudScanHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  background: `linear-gradient(270deg, ${theme.palette.secondary.main}, ${alpha(
    theme.palette.secondary.main,
    0
  )});`,
  borderBottom: `3px solid ${alpha(theme.palette.secondary.contrastText, 0.1)}`,
  [theme.breakpoints.up('sm')]: {
    paddingTop: theme.spacing(6),
  },
}));

export const SectionTitle = styled((props: TextProps) => (
  <Text color="success" variant="h4" {...props} />
))(() => ({}));

export const Tile = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: '5px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

export const TileLabel = styled((props: TextProps) => (
  <Text variant="body2" {...props} />
))({
  fontSize: '13px',
});

export const TileValue = styled((props: TextProps) => (
  <Text variant="body1" {...props} />
))({
  fontSize: '15px',
});

export const ShortStack = styled(Stack)(({ theme }) => ({
  minHeight: '56px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: '5px',
}));

export const TallStack = styled(Stack)(({ theme }) => ({
  minHeight: '118px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: '5px',
}));

export const BorderedStack = styled(Stack)(({ theme }) => ({
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: '5px',
}));

export const VerticalDivider = styled(Divider)(({ theme }) => ({
  height: '70%',
  borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

export const SecondaryTallStack = styled(TallStack)(({ theme }) => ({
  gap: theme.spacing(1),
  flexDirection: 'column',
  marginTop: theme.spacing(1),

  [theme.breakpoints.down('md')]: {
    border: 'none',
  },

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: 0,
  },
}));

export const TertiaryTallStack = styled(TallStack)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    width: '50%',
    border: 'none',
  },
}));

export const ImageContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    position: 'absolute',
    right: theme.spacing(4),
    bottom: theme.spacing(-3),
  },
}));

interface FraudGaugeProps {
  fraudScore?: number | null;
  title?: string;
  tooltipText?: string;
}

export const FraudGauge: React.FC<FraudGaugeProps> = ({
  fraudScore,
  tooltipText,
  title = 'FraudScan Score',
}: FraudGaugeProps) => {
  const theme = useTheme();

  const generateFraudScoreColor = (score: number): string => {
    if (score < 50) return theme.palette.threatDetection?.dark ?? '';
    if (score >= 50 && score < 85) return theme.palette.premium.main;
    if (score >= 85) return theme.palette.lineChart.dark;
    return theme.palette.barChart.light;
  };

  return (
    <TallStack alignItems="center" justifyContent="space-around" width="100%">
      <Stack alignItems="center" direction="row">
        <Text
          variant="h5"
          mb={0}
          mr={0.5}
          fontSize={{
            xs: theme.typography.body2.fontSize,
            lg: theme.typography.h5.fontSize,
          }}
        >
          {title}
        </Text>
        {tooltipText && (
          <Tooltip arrow enterTouchDelay={700} title={tooltipText}>
            <Icons.Help sx={{ fontSize: '1rem' }} />
          </Tooltip>
        )}
      </Stack>
      <Text
        fontWeight="700"
        sx={{ lineHeight: '0' }}
        fontSize={{
          xs: theme.typography.body2.fontSize,
          lg: theme.typography.h5.fontSize,
        }}
      >
        {fraudScore != null ? `${fraudScore}%` : '-'}
      </Text>
      <Stack mb={1} width="70%" height="10px">
        <LinearGaugeChart
          color={generateFraudScoreColor(fraudScore ?? 0)}
          value={fraudScore ?? 0}
        />
      </Stack>
    </TallStack>
  );
};

interface ThreatItemProps {
  text: string;
  tooltipText: string;
  width: string;
  threatLevel?: ThreatLevel;
  textTestId?: string;
}

export const ThreatItem: React.FC<ThreatItemProps> = ({
  text,
  tooltipText,
  width,
  threatLevel,
  textTestId,
}: ThreatItemProps) => {
  const theme = useTheme();

  const threatLevelIcon = (level: ThreatLevel) => {
    const iconsSx = {
      marginBottom: { xs: 1, lg: 2.25 },
      fontSize: '36px',
    };

    const levelIcons = {
      high: (
        <Icons.WarningTwoTone
          sx={{ color: theme.palette.lineChart.dark, ...iconsSx }}
        />
      ),
      medium: (
        <Icons.WarningTwoTone
          sx={{ color: theme.palette.premium.main, ...iconsSx }}
        />
      ),
      low: (
        <Icons.CheckCircleTwoTone
          sx={{ color: theme.palette.threatDetection?.dark, ...iconsSx }}
        />
      ),
      unknown: (
        <Icons.HelpOutlineTwoTone
          sx={{ color: theme.palette.barChart.light, ...iconsSx }}
        />
      ),
    };

    const icon = levelIcons[level] || (
      <Icons.CheckCircleTwoTone
        sx={{ color: theme.palette.barChart.light, ...iconsSx }}
      />
    );

    return icon;
  };
  return (
    <Stack
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        width,
        padding: theme.spacing(1),
      }}
    >
      {threatLevel && threatLevelIcon(threatLevel)}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="center"
        gap={0.5}
      >
        <Text
          textAlign="center"
          variant="h5"
          component="p"
          fontSize={{
            xs: theme.typography.body2.fontSize,
            lg: theme.typography.h5.fontSize,
          }}
          margin={0}
          data-cy={textTestId}
        >
          {text}
        </Text>
        <Tooltip arrow enterTouchDelay={700} title={tooltipText}>
          <Icons.Help
            sx={(th) => ({ fontSize: '1rem', marginLeft: th.spacing(0.5) })}
          />
        </Tooltip>
      </Stack>
    </Stack>
  );
};

interface TileItemProps {
  title: string;
  text: string;
  IconComponent: React.ElementType;
  tooltipText?: string;
  testId?: string;
  testLabelId?: string;
  testContentId?: string;
}

export const TileItem: React.FC<TileItemProps> = ({
  title,
  text,
  tooltipText,
  IconComponent,
  testId,
  testLabelId,
  testContentId,
}: TileItemProps) => {
  const theme = useTheme();

  return (
    <Tile>
      <IconComponent color="primary" sx={{ fontSize: '22px' }} />
      <Stack ml={1.25} mr={1} data-cy={testId}>
        <Text
          sx={{ display: 'flex', alignItems: 'center' }}
          data-cy={testLabelId}
          variant="body2"
          fontSize="13px"
        >
          {title}
          {tooltipText && (
            <Tooltip arrow enterTouchDelay={700} title={tooltipText}>
              <Icons.Help
                sx={{ fontSize: '1rem', marginLeft: theme.spacing(0.5) }}
              />
            </Tooltip>
          )}
        </Text>
        <Text
          sx={{ textOverflow: 'ellipsis', wordBreak: 'break-word' }}
          fontSize={{ xs: '14px', md: '15px' }}
          variant="body1"
          data-cy={testContentId}
        >
          {text}
        </Text>
      </Stack>
    </Tile>
  );
};

export const NewSectionTag = styled((props: TextProps) => (
  <Text
    component="span"
    variant="body2"
    fontWeight="bold"
    fontSize="10px"
    color="primary"
    sx={(theme) => ({
      backgroundColor: theme.palette.premium.main,
      padding: `${theme.spacing(0.4)} ${theme.spacing(1)}`,
      borderRadius: theme.spacing(0.6),
    })}
    {...props}
  >
    NEW
  </Text>
))({});
